import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import scrollToSection from '../../utils/scrollToSection';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 0;
  }
`;

const Container = styled.div`
  width: ${({ $scrolled }) => ($scrolled ? '390px' : '100%')};
  min-height: ${({ $scrolled }) => ($scrolled ? '58px' : '96px')};
  margin: 0 auto;
  padding: ${({ $scrolled }) => ($scrolled ? '0 14px' : '0 clamp(44px, 6vw, 88px)')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $scrolled }) => ($scrolled ? '0' : '42px')};
  pointer-events: auto;
  color: ${({ theme }) => theme.colors.ink};
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(255, 255, 255, 0.94)' : 'rgba(255, 255, 255, 0)'};
  border: ${({ $scrolled }) => ($scrolled ? '1px solid rgba(0, 0, 0, 0.06)' : '0 solid transparent')};
  border-radius: ${({ $scrolled }) => ($scrolled ? '999px' : '0')};
  box-shadow:
    ${({ $scrolled }) =>
      $scrolled
        ? '0 16px 36px rgba(15, 15, 20, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)'
        : 'none'};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(18px) saturate(150%)' : 'none')};
  -webkit-backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(18px) saturate(150%)' : 'none')};
  transition:
    width 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    min-height 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    padding 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    gap 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.35s ease,
    border-color 0.35s ease,
    box-shadow 0.35s ease,
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  transform: ${({ $scrolled }) => ($scrolled ? 'translateY(20px)' : 'translateY(0)')};

  @media (max-width: 768px) {
    width: 100%;
    min-height: 64px;
    padding: 0 24px;
    justify-content: center;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    transform: none;
    /* The transparent top bar must not swallow taps meant for the page. */
    pointer-events: none;
  }
`;

const LogoLink = styled.a`
  cursor: pointer;
  flex: 0 0 auto;
  overflow: hidden;
  max-width: ${({ $scrolled }) => ($scrolled ? '0' : '220px')};
  opacity: ${({ $scrolled }) => ($scrolled ? 0 : 1)};
  transform: ${({ $scrolled }) => ($scrolled ? 'translateX(-8px)' : 'translateX(0)')};
  pointer-events: ${({ $scrolled }) => ($scrolled ? 'none' : 'auto')};
  transition:
    max-width 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.25s ease,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: 768px) {
    max-width: 260px;
    opacity: 1;
    transform: none;
    pointer-events: auto;
    margin: 0 auto;
    text-align: center;
  }
`;

const LogoText = styled.span`
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 500;
  font-size: clamp(1.08rem, 1.4vw, 1.24rem);
  line-height: 1;
  letter-spacing: -0.055em;
  color: ${({ theme }) => theme.colors.ink};
  display: inline-block;
  white-space: pre;
  transition: color 0.25s ease;

  ${LogoLink}:hover & {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

/* Each letter snaps up into place with a quick left-to-right cascade.
   Only opacity/transform animate — both compositor-friendly, no jank. */
const LogoLetter = styled.span`
  display: inline-block;
  opacity: ${({ $revealed }) => ($revealed === false ? 0 : 1)};
  transform: ${({ $revealed }) =>
    $revealed === false ? 'translateY(0.4em)' : 'translateY(0)'};
  transition:
    opacity 0.3s ease-out,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $revealed, $index, $baseDelay }) =>
    $revealed === false ? '0ms' : `${$baseDelay + $index * 32}ms`};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BRAND = 'Jabez Llave';

const BrandMark = ({ revealed = true, baseDelay = 0 }) => (
  <LogoText $revealed={revealed}>
    {BRAND.split('').map((char, i) => (
      <LogoLetter key={i} $revealed={revealed} $index={i} $baseDelay={baseDelay}>
        {char}
      </LogoLetter>
    ))}
  </LogoText>
);

const Menu = styled.ul`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $scrolled }) => ($scrolled ? '0' : '54px')};
  flex: ${({ $scrolled }) => ($scrolled ? '1 1 100%' : '0 1 auto')};
  width: ${({ $scrolled }) => ($scrolled ? '100%' : '520px')};
  margin-left: ${({ $scrolled }) => ($scrolled ? '0' : 'auto')};
  margin-right: ${({ $scrolled }) => ($scrolled ? '0' : 'auto')};
  transition:
    gap 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    flex 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuItem = styled.li``;

const MenuLink = styled.a`
  position: relative;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ $scrolled }) => ($scrolled ? '0.94rem' : '1rem')};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  padding: ${({ $scrolled }) => ($scrolled ? '10px 17px' : '11px 18px')};
  white-space: nowrap;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#eeeeee' : 'transparent')};
  transition:
    background 0.25s ease,
    color 0.25s ease,
    padding 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    font-size 0.35s ease;

  &:after {
    display: none;
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.colors.ink};
    background: #eeeeee;
  }
`;

const DownloadLink = styled.a`
  display: ${({ $scrolled }) => ($scrolled ? 'none' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  min-width: 154px;
  height: 56px;
  padding: 0 30px;
  border-radius: 999px;
  background: #0c0c11;
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: -0.015em;
  white-space: nowrap;
  transition: transform 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    background: #000000;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 1rem;
  cursor: pointer;
  padding: 8px;
  line-height: 0;
  margin-right: -8px;

  @media (max-width: 768px) {
    display: none;
    align-items: center;
    gap: 8px;
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 0.78rem;
    font-weight: 500;
    width: auto;
    justify-content: center;
    padding: 8px;
    margin-right: -8px;
  }
`;

const MobileButtonLinks = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: none;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    padding-right: 16px;
    color: ${({ theme }) => theme.colors.ink};
  }
`;

const MobileButtonLabel = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: inline;
  }
`;

const MobileBottomNav = styled.nav`
  display: none;

  @media (max-width: 768px) {
    position: fixed;
    left: 50%;
    bottom: max(20px, env(safe-area-inset-bottom));
    z-index: 110;
    transform: translateX(-50%);
    width: min(672px, calc(100vw - 80px));
    height: 82px;
    padding: 14px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    pointer-events: auto;
    background: rgba(255, 255, 255, 0.96);
    border-radius: 999px;
    box-shadow: 0 18px 55px rgba(12, 12, 17, 0.09);
    backdrop-filter: blur(18px) saturate(150%);
    -webkit-backdrop-filter: blur(18px) saturate(150%);
  }

  @media (max-width: 430px) {
    width: calc(100vw - 30px);
    height: 64px;
    padding: 8px 12px;
  }
`;

const MobileBottomLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 54px;
  padding: 0 22px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1.12rem;
  font-weight: 500;
  letter-spacing: -0.05em;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#eeeeee' : 'transparent')};
  transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 430px) {
    height: 48px;
    padding: 0 13px;
    font-size: 0.98rem;
  }
`;

const MobileOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.background};
  z-index: 200;
  display: flex;
  flex-direction: column;
  padding: 14px 24px 24px;
`;

const MobileHeader = styled.div`
  width: 100%;
  min-height: 42px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 7px;
  box-shadow:
    0 16px 34px rgba(15, 23, 42, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.48);
  backdrop-filter: blur(18px) saturate(150%);
  -webkit-backdrop-filter: blur(18px) saturate(150%);
`;

const MobileClose = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 1rem;
  cursor: pointer;
  padding: 8px;
  line-height: 0;
  margin-right: -8px;
`;

const MobileList = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
`;

const MobileLink = styled.a`
  display: block;
  padding: 18px 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 600;
  font-size: 2.4rem;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.rule};

  &.active {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'projects', label: 'Works' },
  { id: 'contact', label: 'Contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  // While a nav-driven smooth scroll is in flight the spy must stay quiet,
  // otherwise the highlight flickers through every section it passes.
  const spyFrozenRef = useRef(false);
  const spyReleaseTimer = useRef(null);
  // Hidden at mount only while the splash is running; the loader's tail writes it in.
  const [brandRevealed, setBrandRevealed] = useState(
    () => !(typeof window !== 'undefined' && window.__splashActive)
  );
  const [revealBaseDelay, setRevealBaseDelay] = useState(0);

  const goTo = (id) => {
    setActiveLink(id);
    spyFrozenRef.current = true;
    clearTimeout(spyReleaseTimer.current);
    spyReleaseTimer.current = setTimeout(() => {
      spyFrozenRef.current = false;
    }, 1600);
    scrollToSection(id);
  };

  useEffect(() => {
    if (brandRevealed) return undefined;

    const reveal = (event) => {
      const { delay = 0 } = (event && event.detail) || {};
      setRevealBaseDelay(delay);
      setBrandRevealed(true);
    };

    window.addEventListener('brand-reveal', reveal);
    const safety = setTimeout(() => reveal({ detail: { duration: 250, delay: 0 } }), 2600);
    return () => {
      window.removeEventListener('brand-reveal', reveal);
      clearTimeout(safety);
    };
  }, [brandRevealed]);

  useEffect(() => {
    localStorage.setItem('menuIsOpen', isOpen ? 'true' : 'false');
  }, [isOpen]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40);
          if (!spyFrozenRef.current) {
            for (const section of LINKS.map((l) => l.id)) {
              const el = document.getElementById(section);
              if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.top <= 120 && rect.bottom >= 120) {
                  setActiveLink(section);
                  break;
                }
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const linkVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <Nav $scrolled={scrolled}>
      <Container $scrolled={scrolled}>
        <LogoLink
          href="#home"
          $scrolled={scrolled}
          onClick={(e) => {
            e.preventDefault();
            goTo('home');
          }}
        >
          <BrandMark revealed={brandRevealed} baseDelay={revealBaseDelay} />
        </LogoLink>

        <Menu $scrolled={scrolled}>
          {LINKS.map((link) => (
            <MenuItem key={link.id}>
              <MenuLink
                href={`#${link.id}`}
                className={activeLink === link.id ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  goTo(link.id);
                }}
                $scrolled={scrolled}
                $active={activeLink === link.id}
              >
                {link.label}
              </MenuLink>
            </MenuItem>
          ))}
        </Menu>

        <Burger onClick={() => setIsOpen(true)} aria-label="Open menu" $scrolled={scrolled}>
          <MobileButtonLinks $scrolled={scrolled}>
            {LINKS.map((link) => (
              <span key={link.id}>{link.label}</span>
            ))}
          </MobileButtonLinks>
          <MobileButtonLabel $scrolled={scrolled}>Menu</MobileButtonLabel>
        </Burger>

        <DownloadLink href="/resume.pdf" download="Jabez_Llave_Resume.pdf" $scrolled={scrolled}>
          Download me
        </DownloadLink>
      </Container>

      <MobileBottomNav aria-label="Primary mobile navigation">
        {LINKS.map((link) => (
          <MobileBottomLink
            key={link.id}
            href={`#${link.id}`}
            $active={activeLink === link.id}
            className={activeLink === link.id ? 'active' : ''}
            onClick={(e) => {
              e.preventDefault();
              goTo(link.id);
            }}
          >
            {link.label}
          </MobileBottomLink>
        ))}
      </MobileBottomNav>

      <AnimatePresence>
        {isOpen && (
          <MobileOverlay
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <MobileHeader>
              <LogoLink
                href="#home"
                onClick={(e) => {
                  e.preventDefault();
                  setIsOpen(false);
                  goTo('home');
                }}
              >
                <BrandMark />
              </LogoLink>
              <MobileClose onClick={() => setIsOpen(false)} aria-label="Close menu">
                <FaTimes />
              </MobileClose>
            </MobileHeader>

            <MobileList>
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.id}
                  custom={i}
                  variants={linkVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <MobileLink
                    href={`#${link.id}`}
                    className={activeLink === link.id ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      goTo(link.id);
                    }}
                  >
                    {link.label}
                  </MobileLink>
                </motion.div>
              ))}
            </MobileList>
          </MobileOverlay>
        )}
      </AnimatePresence>
    </Nav>
  );
};

export default Navbar;
